import { useState, type ReactNode } from 'react';
import { Pencil, X } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Input } from '@reflex/ui/components/ui/input';
import { Label } from '@reflex/ui/components/ui/label';
import { Textarea } from '@reflex/ui/components/ui/textarea';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { CanGuard } from '@reflex/ui/client/permissions';
import { DirtyActionBar } from '@reflex/ui/components/shared/DirtyActionBar';

interface FieldConfig {
  /** Visible label rendered next to the input. */
  label: string;
  /** Input id used by Label/htmlFor. */
  id: string;
  /** Current value. */
  value: string;
  /** Change handler. Required when the field is editable. */
  onChange?: (value: string) => void;
  /** When true, the input renders disabled + readOnly. */
  readOnly?: boolean;
  /** Optional input maxLength. */
  maxLength?: number;
  /** Optional helper text shown below the input. */
  hint?: string;
  /** Optional placeholder. */
  placeholder?: string;
}

interface DescriptionFieldConfig extends FieldConfig {
  /** Textarea rows. Default 2. */
  rows?: number;
}

interface EntityDetailsCardProps {
  /** Section card title. Default: 'Details'. */
  title?: string;
  /** Section card description. */
  description?: string;
  /** Optional icon shown in the section header. */
  icon?: ReactNode;
  /** Stable testid on the section root. */
  'data-testid'?: string;

  /** Name field (left column of the top grid). */
  name: FieldConfig;
  /** Identifier / slug field (right column of the top grid). */
  identifier: FieldConfig;
  /** Optional Description textarea rendered full-width below the grid. */
  descriptionField?: DescriptionFieldConfig;

  /** Lock every editable input. Overrides per-field `readOnly`. */
  disabled?: boolean;

  /** Whether the form has unsaved changes. */
  dirty: boolean;
  /** Whether a save is in flight. */
  saving?: boolean;
  /** Save handler. */
  onSave: () => void;
  /** Discard / reset handler. */
  onDiscard: () => void;
  /** Optional permission that gates the save footer. */
  savePermission?: string;
  /**
   * Team `savePermission` is evaluated within, for a card that edits one
   * team. Matches the server, which checks these routes with `canInTeam`.
   */
  savePermissionTeam?: string;
  /**
   * When true, the card starts collapsed into a read-only summary with a
   * pencil "Edit" affordance. Clicking it reveals the existing edit fields +
   * action bar; saving or discarding collapses back to the summary. Defaults
   * to `false`, which keeps the always-editable layout.
   */
  collapsible?: boolean;
  /**
   * When true, skips rendering the built-in `DirtyActionBar` so the caller
   * can render it elsewhere (e.g. a page-level footer that also saves
   * changes from other sections like role permissions).
   */
  hideFooter?: boolean;
}

/**
 * Shared "Details" card for entity editor pages (organization, team, role).
 *
 * Renders the standard Name + Identifier grid with an optional Description
 * Textarea, and a built-in `DirtyActionBar` that can be hidden when the
 * caller wires a page-level footer.
 *
 * Pass `collapsible` to start in a read-only summary with a pencil "Edit"
 * button that swaps in the edit fields on demand.
 */
export function EntityDetailsCard({
  title = 'Details',
  description,
  icon,
  'data-testid': dataTestId,
  name,
  identifier,
  descriptionField,
  disabled = false,
  dirty,
  saving,
  onSave,
  onDiscard,
  savePermission,
  savePermissionTeam,
  collapsible = false,
  hideFooter = false,
}: EntityDetailsCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const editing = !collapsible || isEditing;

  function handleSave() {
    onSave();
    if (collapsible) setIsEditing(false);
  }

  function handleDiscard() {
    onDiscard();
    if (collapsible) setIsEditing(false);
  }

  const editToggle =
    collapsible && !disabled ? (
      <EditToggle
        editing={isEditing}
        onEdit={() => setIsEditing(true)}
        onCancel={handleDiscard}
        permission={savePermission}
        permissionTeam={savePermissionTeam}
      />
    ) : undefined;

  return (
    <SectionCard
      title={title}
      description={description}
      icon={icon}
      action={editToggle}
      data-testid={dataTestId}
    >
      <div className="space-y-4">
        {editing ? (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField field={name} disabled={disabled} />
              <TextField field={identifier} disabled={disabled} />
            </div>
            {descriptionField && (
              <TextField field={descriptionField} disabled={disabled} multiline />
            )}
            {!hideFooter && (
              <DirtyActionBar
                dirty={dirty}
                saving={saving}
                onDiscard={handleDiscard}
                onSave={handleSave}
                permission={savePermission}
                permissionTeam={savePermissionTeam}
              />
            )}
          </>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <ReadOnlyField field={name} />
            <ReadOnlyField field={identifier} />
            {descriptionField && (
              <div className="md:col-span-2">
                <ReadOnlyField field={descriptionField} />
              </div>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

function EditToggle({
  editing,
  onEdit,
  onCancel,
  permission,
  permissionTeam,
}: {
  editing: boolean;
  onEdit: () => void;
  onCancel: () => void;
  permission?: string;
  permissionTeam?: string;
}) {
  const button = editing ? (
    <Button type="button" variant="ghost" size="sm" onClick={onCancel} aria-label="Cancel editing">
      <X className="icon-sm" /> Cancel
    </Button>
  ) : (
    <Button type="button" variant="outline" size="sm" onClick={onEdit} aria-label="Edit details">
      <Pencil className="icon-sm" /> Edit
    </Button>
  );

  if (permission) {
    return (
      <CanGuard permission={permission} team={permissionTeam}>
        {button}
      </CanGuard>
    );
  }
  return button;
}

function ReadOnlyField({ field }: { field: FieldConfig | DescriptionFieldConfig }) {
  const value = field.value.trim();
  return (
    <div className="space-y-1.5">
      <Label>{field.label}</Label>
      <p className="text-sm text-foreground whitespace-pre-wrap break-words">
        {value === '' ? <span className="text-muted-foreground">—</span> : value}
      </p>
    </div>
  );
}

function TextField({
  field,
  disabled,
  multiline = false,
}: {
  field: FieldConfig | DescriptionFieldConfig;
  disabled: boolean;
  multiline?: boolean;
}) {
  const isReadOnly = field.readOnly === true;
  const inputDisabled = disabled || isReadOnly;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={field.id}>{field.label}</Label>
      {multiline ? (
        <Textarea
          id={field.id}
          value={field.value}
          onChange={(e) => field.onChange?.(e.target.value)}
          disabled={inputDisabled}
          readOnly={isReadOnly}
          rows={(field as DescriptionFieldConfig).rows ?? 2}
          maxLength={field.maxLength}
          placeholder={field.placeholder}
        />
      ) : (
        <Input
          id={field.id}
          value={field.value}
          onChange={(e) => field.onChange?.(e.target.value)}
          disabled={inputDisabled}
          readOnly={isReadOnly}
          maxLength={field.maxLength}
          placeholder={field.placeholder}
        />
      )}
      {field.hint && <p className="text-xs text-muted-foreground">{field.hint}</p>}
    </div>
  );
}
